import React, { JSX, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { Email as EmailIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useAuth } from 'hooks/AuthContext';

export const RegistrationSuccessDialog = (props: {
  open: boolean;
  onClose: () => void;
  email: string;
  userType: string;
}): JSX.Element => {
  const { resendEmailVerification, isLoading } = useAuth();
  const [resendMessage, setResendMessage] = useState('');

  const handleResendVerification = async (): Promise<void> => {
    try {
      const success = await resendEmailVerification(props.email);
      if (success) setResendMessage('Verification email sent!');
    } catch (error: unknown) {
      const tmp = error as { message?: string };
      setResendMessage(tmp?.message || 'Network error. Please try again.');
    }
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ textAlign: 'center', p: 4 }}>
        <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Check Your Email
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          We&apos;e sent a verification link to:
        </Typography>
        <Typography variant="h6" color="primary" gutterBottom>
          {props.email}
        </Typography>
        <Alert severity="info" sx={{ mt: 2, mb: 3, textAlign: 'left' }}>
          <Typography variant="body2">
            <strong>Next steps:</strong>
            <br />
            1. Check your email inbox (and spam folder)
            <br />
            2. Click the verification link
            <br />
            3. Your {props.userType} account will be activated
          </Typography>
        </Alert>
        {resendMessage && (
          <Alert severity={resendMessage.includes('sent') ? 'success' : 'error'} sx={{ mb: 2 }}>
            {resendMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Button
          onClick={handleResendVerification}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          Resend Email
        </Button>
        <Button variant="contained" onClick={props.onClose}>
          Got It
        </Button>
      </DialogActions>
    </Dialog>
  );
};
